import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid,
  type LucideIcon,
  Search,
  Table as TableIcon,
} from 'lucide-react';

import { cn } from '@/shared/libs';

import { Input } from './input';

export type ListToolbarViewMode = 'cards' | 'table';

export interface ListToolbarSearchConfig {
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}

export interface ListToolbarViewConfig {
  onChange: (value: ListToolbarViewMode) => void;
  value: ListToolbarViewMode;
}

export interface ListToolbarProps {
  count?: number;
  search?: ListToolbarSearchConfig;
  titleIcon?: LucideIcon;
  total?: number;
  view?: ListToolbarViewConfig;
}

const VIEW_TOGGLE_CLASS: Record<
  ListToolbarViewMode,
  Record<'active' | 'inactive', string>
> = {
  cards: {
    active: 'bg-card text-foreground shadow-sm',
    inactive: 'text-muted-foreground hover:text-foreground',
  },
  table: {
    active: 'bg-card text-foreground shadow-sm',
    inactive: 'text-muted-foreground hover:text-foreground',
  },
};

export const ListToolbar: FC<ListToolbarProps> = ({
  count,
  search,
  titleIcon: TitleIcon,
  total,
  view,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-2">
        {!!TitleIcon && (
          <TitleIcon className="h-4 w-4 shrink-0 text-brand-secondary" />
        )}
        {typeof count === 'number' && (
          <span className="text-xs text-muted-foreground">
            {typeof total === 'number'
              ? t('listToolbar.countShownTotal', { count, total })
              : t('listToolbar.countShown', { count })}
          </span>
        )}
      </div>

      <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:justify-end">
        {!!search && (
          <div className="min-w-0 flex-1 sm:w-64 sm:flex-none">
            <Input
              aria-label={t('listToolbar.search')}
              className="bg-background/60"
              placeholder={
                search.placeholder ?? t('listToolbar.searchPlaceholder')
              }
              startIcon={Search}
              value={search.value}
              onChange={(event) => search.onChange(event.target.value)}
            />
          </div>
        )}

        {!!view && (
          <div className="inline-flex shrink-0 rounded-xl bg-foreground/5 p-1">
            <button
              aria-label={t('listToolbar.cardView')}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                VIEW_TOGGLE_CLASS.cards[
                  view.value === 'cards' ? 'active' : 'inactive'
                ],
              )}
              type="button"
              onClick={() => view.onChange('cards')}>
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
            <button
              aria-label={t('listToolbar.tableView')}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                VIEW_TOGGLE_CLASS.table[
                  view.value === 'table' ? 'active' : 'inactive'
                ],
              )}
              type="button"
              onClick={() => view.onChange('table')}>
              <TableIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

ListToolbar.displayName = 'ListToolbar';
